import { useStore } from './storeHook'
import { useStoreRoute } from './routeHooks'

import { valuesOf } from '../common/tsUtils'

import { myWbColumns } from '../types/wbColumns'
import { myOzonColumns } from '../types/ozonColumns'
import { MyProductColumn } from '../types/myProductColumnTypes'
import { createMyStorageColumns } from '../types/myStorageColumns'
import { myYandexMarketColumns } from '../types/yandexMarketColumns'
import { MyProductColumnGroupId } from '../types/myProductColumnGroups'
import { MyProductColumnId, syncErrorsColumnId } from '../types/myProductColumnIds'
import { AllowedIntegrationsMap, StoreIntegrationType } from '../types/integrationTypeUtils'

import { MyProductState } from '../server/mpsklad_core/Models/MyProductState'
import { IntegrationType } from '../server/mpsklad_core/Entity/IntegrationType'

/**
 * NOTE: Don't track in React effects - a new value is returned every time.
 */
export const useMyProductColumns =
  () => {
    const {allowedIntegrations, hiddenMyProductColumnIds, productStore: {myStateTab}} = useStore()
    const homeHasStoreType = useHomeHasStoreType()

    return createMyProductColumns(allowedIntegrations, hiddenMyProductColumnIds, myStateTab, homeHasStoreType)
  }

/**
 * This is a separate method to allow tests without dependening on router context.
 */
export const createMyProductColumns =
  (allowedIntegrations: AllowedIntegrationsMap,
   hiddenMyProductColumnIds: Set<MyProductColumnId>,
   stateTab: MyProductState,
   homeHasStoreType: (integration: StoreIntegrationType) => boolean) => {
    const groupedColumns = {
      [MyProductColumnGroupId.MyStorage]:
        createMyStorageColumns(homeHasStoreType, allowedIntegrations),

      [MyProductColumnGroupId.Ozon]:
        homeHasStoreType(IntegrationType.Ozon) && allowedIntegrations[IntegrationType.Ozon]
        ? myOzonColumns
        : [],

      [MyProductColumnGroupId.Wildberries]:
        homeHasStoreType(IntegrationType.Wildberries) && allowedIntegrations[IntegrationType.Wildberries]
        ? myWbColumns
        : [],

      [MyProductColumnGroupId.YandexMarket]:
        homeHasStoreType(IntegrationType.YandexMarket) && allowedIntegrations[IntegrationType.YandexMarket]
        ? myYandexMarketColumns
        : []
    }

    const allColumns = valuesOf(groupedColumns).flatMap(_ => _)

    const visibleColumns = allColumns.filter(column => !hiddenMyProductColumnIds.has(column.id))

    // Create this object atomically to guarantee reference equality of column array items
    return {
      grouped: groupedColumns,
      all: allColumns,
      // TODO: Move inside TableData?
      visible: stateTab !== MyProductState.Error
               ? visibleColumns.filter(column => column.id !== syncErrorsColumnId)
               : visibleColumns
    } as const satisfies {
      grouped: { readonly [Group in MyProductColumnGroupId]: MyProductColumn[] }
      all: MyProductColumn[]
      visible: MyProductColumn[]
    }
  }

/**
 * NOTE: Don't track in React effects - a new value is returned every time.
 */
export const useHomeHasStoreType = () => {
  const storeRoute = useStoreRoute()
  const {syncStore} = useStore()

  // Can't memoize because of store usage
  return (integration: StoreIntegrationType) =>
    storeRoute?.integration === integration && syncStore.getStoreAccounts(integration).length > 0
}